import { useCallback, useEffect, useRef, useState } from "react";
import Button from "@mp/button";
import { t } from "@mp/locale";
import { useLocale } from "@mp/communication";
import "../styles.scss";
import PreferenceModal from "./PreferenceModal";
import Cookies from "js-cookie";
import { getCookieConsent } from "../utils";
import { ConsentChangeFn, CookieDetailV3, cookieOptionsProps } from "../types";
import { initialState } from "../constants";

declare global {
  interface Window {
    // Sandboxed Javascript code (ex: Google Tag Manager) may not be able to listen for custom
    // events dispatched on `window`, expose a global method for adding callbacks as an alternative
    // @see https://developers.google.com/tag-platform/tag-manager/templates/api#callinwindow
    __MP_CONSENT_ON_CHANGE: (cb: ConsentChangeFn) => void;
  }
}

export interface CookieConsentProps {
  onClose: Function;
  isUS?: boolean;
}

const createCookieEvent = (detail: CookieDetailV3) =>
  new CustomEvent("cookie-consent.changed", { detail });

function createCookie() {
  // Use a global variable for projects that are not reverse proxied
  const location = (window as any).cookiePath || "";
  // Don't need to handle response. All we want it the cookie itself.
  // Even if fetch fails, the client will just create the cookie and it will still work.
  return fetch(`${location}/api/mp/consent-cookie`);
}

const cookieName: string = "cookie_consent_v3";

export const cookieOptions: cookieOptionsProps = {
  domain:
    window.location.host.indexOf("localhost") >= 0
      ? "localhost"
      : "matterport.com",
  expires: 365,
  path: "/",
  sameSite: "lax",
  secure: true,
};

export default function CookieConsent({ isUS, onClose }: CookieConsentProps) {
  const [isModalOpen, setModalOpen] = useState(false);
  const [hasCookie, setHasCookie] = useState(false);
  const onChangeCallbacks = useRef<Set<ConsentChangeFn>>(new Set());
  // Listen for locale changes and refresh
  useLocale();

  useEffect(() => {
    window.__MP_CONSENT_ON_CHANGE = (
      cb: (consentData: CookieDetailV3) => void
    ) => {
      if (typeof cb === "function") {
        onChangeCallbacks.current.add(cb);
      }
    };
  }, []);

  useEffect(() => {
    createCookie().finally(() => {
      checkCookie();
    });
  }, [isUS, hasCookie]);

  const updateConsent = useCallback((consentDetail: CookieDetailV3) => {
    const event = createCookieEvent(consentDetail);
    document.dispatchEvent(event);
    Cookies.set(cookieName, JSON.stringify(consentDetail), cookieOptions);
    onChangeCallbacks.current.forEach((cb) => {
      try {
        cb(consentDetail);
      } catch (e) {
        console.error(e);
      }
    });
  }, []);

  function checkCookie() {
    const cookie = getCookieConsent();
    const isUSChecked = isUS && cookie?.usChecked;
    const isUSAndNotChecked = isUS && cookie?.usChecked === undefined;
    // Has cookie and country not US. No modal
    if (cookie && !isUS) {
      setHasCookie(true);
    }
    // Has cookie, country is US, and we've checked in the past. No modal
    if (cookie && isUSChecked) {
      setHasCookie(true);
    }
    // Country is US and we have not checked, set cookie with full permissions
    // But do not set checked until user interacts with cookie.
    if (isUSAndNotChecked) {
      updateConsent(initialState);
    }
  }

  const modalHandler = () => {
    setModalOpen((currentState) => {
      return (currentState = false);
    });
  };

  function saveAcceptAllCookies() {
    const state = {
      ...initialState,
      ...(isUS ? { usChecked: true } : undefined),
    };
    updateConsent(state);
    setHasCookie(true);
    onClose();
  }

  function savePreferences(preferences: CookieDetailV3) {
    const state = {
      ...preferences,
      ...(isUS ? { usChecked: true } : undefined),
    };
    updateConsent(state);
    setHasCookie(true);
    onClose();
  }

  if (hasCookie) {
    return null;
  }

  return (
    <>
      <PreferenceModal
        isModalOpen={isModalOpen}
        onCloseModal={modalHandler}
        saveAcceptAllCookies={saveAcceptAllCookies}
        savePreferences={savePreferences}
      />

      <div className="mp-cookie-policy-wrapper" id="cookie-policy">
        <p>
          {t("COOKIE_POLICY.message")}{" "}
          <a href="https://matterport.com/cookie-policy">
            {t("COMMON.learnMore")}
          </a>
        </p>
        <div className="mp-cookie-consent-button-wrapper">
          <Button
            htmlId="cookie-only-necessary"
            label={t("COOKIE_POLICY.cookieSettings")}
            light
            className="mp-cookie-consent-button-tertiary"
            buttonType="tertiary"
            onClick={() => setModalOpen(true)}
          />
          <Button
            htmlId="cookie-accept-all"
            label={t("COOKIE_POLICY.acceptAll")}
            className="mp-cookie-consent-button-secondary"
            light
            buttonType="primary"
            onClick={() => {
              saveAcceptAllCookies();
              setHasCookie(true);
            }}
          />
        </div>
      </div>
    </>
  );
}
